import { ShiftsFieldSet, JobsFieldSet } from '@rogoag/airtable/crm';
import { LocalAtRecord, Job } from '../db';
import { syncMulti } from '../db_ops/airtable_record_ops';
import logger from '../logger';
import { alertError, alertSuccess, alertWarn } from '../alertDispatcher';
import { getLocalAtSearch } from '../db_ops/airtable_search_ops';

class ShiftService {
  private dirtyUpdates: LocalAtRecord[] = [];

  async rollOverJobs(
    sourceShift: LocalAtRecord<ShiftsFieldSet>,
    destinationShift: LocalAtRecord<ShiftsFieldSet>,
    jobsToRollover: LocalAtRecord<JobsFieldSet>[],
  ) {
    let destinationShiftLastOrder = this.getShiftLastOrder(destinationShift);
    const filteredJobs = await this.filterOutSampledJobs(jobsToRollover);

    await logger.log('ShiftService - rolloverJobs', {
      sourceShift: { id: sourceShift.id, name: sourceShift.get('Name'), jobsLink: sourceShift.get('Jobs link') },
      destinationShift: {
        id: destinationShift.id,
        name: destinationShift.get('Name'),
        jobsLink: sourceShift.get('Jobs link'),
      },
      jobsToRollover: jobsToRollover.map((job) => ({ id: job.id, name: job.get('Name') })),
      filteredJobs: filteredJobs.map((job) => ({ id: job.id, name: job.get('Name') })),
      destinationShiftLastOrder,
    });

    if (jobsToRollover.length !== filteredJobs.length) {
      alertWarn(
        `${jobsToRollover.length - filteredJobs.length} job(s) were not rolled over because they were already sampled.`,
      );
    }

    for (const job of filteredJobs) {
      // We don't have to unassign jobs from the previous shift, we
      // can just assign a new shift to a job.
      // await this.unassignJobFromShift(freshSourceShiftRecord, job.id);

      await this.assignJobToShift(destinationShift, job, ++destinationShiftLastOrder);
    }

    await this.syncUpdates();
  }

  static async fetchFreshShiftRecord(shiftId: string): Promise<LocalAtRecord<ShiftsFieldSet> | undefined> {
    let freshShiftRecord: LocalAtRecord<ShiftsFieldSet> | undefined = undefined;

    try {
      const search = await getLocalAtSearch<ShiftsFieldSet>('Shifts', '', `RECORD_ID() = '${shiftId}'`);
      await search.refresh();
      const foundRecords = search.getRecords();

      if (Array.isArray(foundRecords) && foundRecords.length === 1) {
        freshShiftRecord = foundRecords[0];
      }
    } catch (e) {
      alertWarn(`Error occurred while trying to fetch a fresh shift record ${shiftId}`);

      return undefined;
    }

    return freshShiftRecord;
  }

  static async unassignJobsFromShift(
    shift: LocalAtRecord<ShiftsFieldSet>,
    isShiftRecordAlreadyFresh: boolean,
    jobsToUnassign: string[],
    syncToAt: boolean = true,
  ) {
    let freshShiftRecord: LocalAtRecord<ShiftsFieldSet> | undefined = isShiftRecordAlreadyFresh ? shift : undefined;

    if (!isShiftRecordAlreadyFresh) {
      freshShiftRecord = await ShiftService.fetchFreshShiftRecord(shift.id);
    }

    if (!freshShiftRecord) {
      alertWarn(`Could not fetch fresh shift record from AT`);

      return;
    }

    const currentShiftJobIds = freshShiftRecord.get('Jobs link');

    if (!Array.isArray(currentShiftJobIds) || currentShiftJobIds.length === 0) {
      return;
    }

    // Important to have a fresh record here, otherwise some jobs inside "Jobs link"
    // may be unassigned from shift.
    freshShiftRecord.set(
      'Jobs link',
      currentShiftJobIds.slice().filter((jobId) => !jobsToUnassign.includes(jobId)),
    );

    if (syncToAt) {
      freshShiftRecord.sync();
    }
  }

  private async filterOutSampledJobs(jobs: LocalAtRecord<JobsFieldSet>[]): Promise<LocalAtRecord<JobsFieldSet>[]> {
    let freshRecordsByIds: Record<string, { id: string; name: string; sampled: string }> = {};

    try {
      freshRecordsByIds = (await Job.getJobRecordsByIds(jobs.map((job) => job.id))).reduce((acc, cur) => {
        acc[cur.id] = {
          id: cur.id,
          name: cur.get('Name'),
          sampled: cur.get('Sample Date'),
        };

        return acc;
      }, {});
    } catch (e) {
      alertWarn(`Error occurred while fetching fresh job records`);

      return [];
    }

    return jobs.filter((job) => {
      if (!freshRecordsByIds[job.id]) {
        return false;
      }

      if (freshRecordsByIds[job.id].sampled) {
        return false;
      }

      return true;
    });
  }

  private async syncUpdates() {
    try {
      const results = await syncMulti(this.dirtyUpdates);

      await logger.log(
        'ShiftService - synced updates',
        results.map((r) => ({ id: r.id, table: r._table.name })),
      );

      alertSuccess(`${results.filter((r) => r._table.name === 'Jobs').length} job(s) rolled over successfully`);
    } catch (error) {
      await logger.log('ShiftService - syncing error', error);

      alertError('Error rolling over jobs');
    }

    this.dirtyUpdates = [];
  }

  private async unassignJobFromShift(freshShiftRecord: LocalAtRecord<ShiftsFieldSet>, jobId: string) {
    if (!freshShiftRecord) {
      return;
    }

    await ShiftService.unassignJobsFromShift(freshShiftRecord, true, [jobId], false);

    const alreadyInDirtyUpdates = this.dirtyUpdates.find((update) => update.id === freshShiftRecord.id);

    if (alreadyInDirtyUpdates) {
      // We need to extract it and push the newly updated record to the end of the array,
      // so that when we sync, the last update is the one that gets synced.
      this.dirtyUpdates = this.dirtyUpdates.filter((update) => update.id !== freshShiftRecord.id);
    }

    this.dirtyUpdates.push(freshShiftRecord);
  }

  private async assignJobToShift(
    shift: LocalAtRecord<ShiftsFieldSet>,
    job: LocalAtRecord<JobsFieldSet>,
    order: number,
  ) {
    const robot = shift.getFirst('Robot');

    await job.set('Shift', [shift.id]);
    await job.set('Order', order);

    await logger.log('ShiftService - assignJobToShift', { shiftId: shift.id, jobId: job.id, order, robot });

    this.dirtyUpdates.push(job);
  }

  private getShiftLastOrder(shift: LocalAtRecord<ShiftsFieldSet>): number {
    const jobsOrderIdsStr = shift.get('Order IDs') as string;

    if (!jobsOrderIdsStr) {
      return -1;
    }

    const jobsOrderIds = jobsOrderIdsStr.split(',').sort();

    if (jobsOrderIds.length === 0) {
      return -1;
    }

    return parseInt(jobsOrderIds[jobsOrderIds.length - 1]);
  }
}

export default ShiftService;
